import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  CAvatar,
  CButton,
  CCard,
  CCardBody,
  CCardHeader,
  COffcanvas,
  COffcanvasBody,
  COffcanvasHeader,
  COffcanvasTitle,
  CBadge,
  CSpinner,
} from "@coreui/react";
import CIcon from "@coreui/icons-react";
import { cilPencil, cilTrash, cilX, cilZoom } from "@coreui/icons";

import { apiClient } from "services/Api";
import DataTableComponent from "components/DataTableComponent";
import ToastNotification from "components/globals/ToastNotification";
import userAvatar from "assets/images/avatars/user.png";

const IntellectualPropertyList = () => {
  const navigate = useNavigate();
  const [toast, setToast] = useState(null);
  const [items, setItems] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [previewItem, setPreviewItem] = useState(null);

  const handleNavigate = (path) => {
    setPreviewItem(null);
    setTimeout(() => navigate(path), 0);
  };

  useEffect(() => {
    const fetchItems = async () => {
      setIsLoading(true);
      try {
        const response = await apiClient.get(`/intellectual-properties/`);
        setItems(response.data.intellectual_properties || []);
      } catch (error) {
        setToast({
          title: "Propriedade Intelectual",
          message: "Erro a carregar as propriedades intelectuais",
          type: "error",
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchItems();
  }, []);

  const handleDelete = async (itemId) => {
    if (!window.confirm("Tem a certeza que quer eliminar este registo?")) {
      return;
    }

    try {
      await apiClient.delete(`/intellectual-properties/${itemId}/`);
      setItems(items.filter((item) => item.id !== itemId));
      setPreviewItem(null);
    } catch (error) {
      setToast({
        title: "Propriedade Intelectual",
        message: "Erro ao eliminar a propriedade intelectual",
        type: "error",
      });
    }
  };

  const columns = [
    {
      name: "Data",
      selector: (row) => row.created_at || "-",
      sortable: true,
      className: "text-center",
    },
    {
      name: "Designação",
      selector: (row) => row.designation || "-",
      sortable: true,
      grow: 2,
    },
    {
      name: "Abrangência",
      selector: (row) => row.scope || "-",
      sortable: true,
    },
    {
      name: "Ativa",
      selector: (row) => (row.active ? "Sim" : "Não"),
      sortable: true,
      className: "text-center",
    },
    {
      name: "Prazo de validade/caducidade",
      selector: (row) => row.expiration_date || "-",
      sortable: true,
      className: "text-center",
    },
    {
      name: "Criado por",
      selector: (row) => row.created_by_name || "-",
      sortable: true,
    },
  ];

  return (
    <>
      <ToastNotification toast={toast} setToast={setToast} />

      <CCard>
        <CCardHeader className="d-flex justify-content-between align-items-center">
          <h3 className="mb-0">Propriedade Intelectual</h3>
          <Link to="/propriedade-intelectual/new">
            <CButton color="info" className="text-white">
              Nova Propriedade
            </CButton>
          </Link>
        </CCardHeader>
        <CCardBody>
          {isLoading ? (
            <div className="text-center">
              <CSpinner color="primary" />
            </div>
          ) : (
            <DataTableComponent
              columns={columns}
              data={items}
              onRowClicked={(row) => setPreviewItem(row)}
            />
          )}
        </CCardBody>
      </CCard>

      {previewItem && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "transparent",
            zIndex: 1040,
          }}
          onClick={() => setPreviewItem(null)}
          aria-hidden="true"
        />
      )}

      <COffcanvas
        placement="end"
        visible={Boolean(previewItem)}
        onHide={() => setPreviewItem(null)}
        backdrop={false}
        scrollable
        scroll
        style={{ width: "560px", maxWidth: "95vw" }}
      >
        <COffcanvasHeader className="border-bottom">
          <COffcanvasTitle className="d-flex align-items-center me-auto">
            <div>
              <div className="fw-semibold">Propriedade Intelectual</div>
              <div className="text-medium-emphasis small">
                #{previewItem?.id || "-"}
              </div>
            </div>
          </COffcanvasTitle>
          <div className="d-flex align-items-center gap-2">
            {previewItem && (
              <>
                <CButton
                  color="info"
                  variant="outline"
                  size="sm"
                  onClick={() =>
                    handleNavigate(
                      `/propriedade-intelectual/${previewItem.id}?view=1`,
                    )
                  }
                >
                  <CIcon icon={cilZoom} className="me-1" />
                  Ver
                </CButton>
                <CButton
                  color="info"
                  size="sm"
                  className="text-white"
                  onClick={() =>
                    handleNavigate(`/propriedade-intelectual/${previewItem.id}`)
                  }
                >
                  <CIcon icon={cilPencil} className="me-1" />
                  Editar
                </CButton>
                <CButton
                  color="danger"
                  size="sm"
                  className="text-white"
                  onClick={() => handleDelete(previewItem.id)}
                >
                  <CIcon icon={cilTrash} className="me-1" />
                  Eliminar
                </CButton>
              </>
            )}
            <CButton
              color="link"
              className="text-body p-0"
              onClick={() => setPreviewItem(null)}
              title="Fechar"
            >
              <CIcon icon={cilX} size="lg" />
            </CButton>
          </div>
        </COffcanvasHeader>
        <COffcanvasBody>
          {previewItem ? (
            <div className="d-flex flex-column gap-3">
              <div className="d-flex align-items-center gap-3">
                <CAvatar
                  src={previewItem.created_by_avatar_url || userAvatar}
                  size="lg"
                />
                <div className="flex-grow-1">
                  <div className="fw-semibold">
                    {previewItem.created_by_name || "-"}
                  </div>
                  <div className="text-medium-emphasis small">
                    {previewItem.created_at || "-"}
                  </div>
                </div>
                <CBadge color={previewItem.active ? "success" : "secondary"}>
                  {previewItem.active ? "Ativa" : "Inativa"}
                </CBadge>
              </div>

              <div className="border rounded-3 p-3 bg-body-tertiary">
                <div className="text-uppercase text-medium-emphasis small">
                  Tipo
                </div>
                <div className="fw-semibold">{previewItem.type || "-"}</div>
              </div>

              <div className="border rounded-3 p-3 bg-body-tertiary">
                <div className="text-uppercase text-medium-emphasis small">
                  Designação
                </div>
                <div className="fw-semibold">
                  {previewItem.designation || "-"}
                </div>
              </div>

              <div className="border rounded-3 p-3 bg-body-tertiary">
                <div className="text-uppercase text-medium-emphasis small">
                  Abrangência
                </div>
                <div className="fw-semibold">{previewItem.scope || "-"}</div>
              </div>

              <div className="border rounded-3 p-3 bg-body-tertiary">
                <div className="text-uppercase text-medium-emphasis small">
                  Data de registo
                </div>
                <div className="fw-semibold">
                  {previewItem.registration_date || "-"}
                </div>
              </div>

              <div className="border rounded-3 p-3 bg-body-tertiary">
                <div className="text-uppercase text-medium-emphasis small">
                  Prazo de validade/caducidade
                </div>
                <div className="fw-semibold">
                  {previewItem.expiration_date || "-"}
                </div>
              </div>
            </div>
          ) : (
            <div className="text-muted">
              Clique numa propriedade intelectual para ver o detalhe.
            </div>
          )}
        </COffcanvasBody>
      </COffcanvas>
    </>
  );
};

export default IntellectualPropertyList;
